import React, { useState, useEffect, useRef, useMemo, createContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import SurveyList from './components/SurveyList';

// SurveyData type for use in SurveyList and dashboards
export type SurveyData = {
  id: string;
  title: string;
  description?: string;
  createdBy: string;
  createdAt: string;
  updatedAt?: string;
  isPublished?: boolean;
  isDeleted?: boolean;
  [key: string]: any;
};
import CreateSurvey from './components/CreateSurvey';
import EditSurvey from './components/EditSurvey';
import ViewResponses from './components/ViewResponses';
import CampaignManagement from './components/CampaignManagement';
import ECMDashboard from './components/ECMDashboard';
import NewCampaign from './components/NewCampaign';
import JobDetails from './components/JobDetails';
import ECMRoutesWrapper from './components/ECMRoutesWrapper';
import { RoleBasedDashboard } from './components/RoleBasedDashboard';
import { TeacherDashboard } from './components/TeacherDashboard';
import { FacultyDashboard } from './components/FacultyDashboard';
import { ViewerDashboard } from './components/ViewerDashboard';
import { CreatorDashboard } from './components/CreatorDashboard';
import { CampaignManagerDashboard } from './components/CampaignManagerDashboard';
import { SchedulerAdminDashboard } from './components/scheduler/SchedulerAdminDashboard';
import { InstructorScheduleView } from './components/scheduler/InstructorScheduleView';
import { ConstraintsEditor } from './components/scheduler/ConstraintsEditor';
import { EnrollmentVerification } from './components/EnrollmentVerification';
import { EnrollmentVerificationBypass } from './components/EnrollmentVerificationBypass';
import { StudentDashboard } from './components/StudentDashboard';
import { LoginButton, getCurrentUser, isAuthenticated, handleRedirectCallback, attemptSilentAuth, logout } from './auth/LoginButton';
import { freshAccessToken } from './auth/accessToken';
import { RoleRoute } from './rbac/RoleRoute';
import { getHomeRoute } from './rbac/roles';
import { schedulerApi } from './services/scheduler-api';
import { applyTheme, getSelectedTheme, themes } from './themes';
import { ThemeSelector } from './ThemeSelector';
import MobilePreview from './components/MobilePreview';
import { createTheme } from '@mui/material/styles';

const isDarkTheme = (themeName: string): boolean => themeName === 'midnight' || themeName === 'navy';
const getLogoPath = (themeName: string): string => isDarkTheme(themeName)
  ? '/images/Logo with blue as white.png'
  : '/images/Transparent full color logo.png';

export const PendingDashboardNavContext = createContext({ pendingDashboardNav: false, setPendingDashboardNav: (_: boolean) => {} });

// Protected Route Component with Layout
function ProtectedRoute({ children, currentTheme, handleThemeChange }: { children: React.ReactNode, currentTheme: string, handleThemeChange: (theme: string) => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [user] = useState(getCurrentUser());
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const IDLE_TIMEOUT_MS = 60 * 60 * 1000;   // 60 minutes
  const WARNING_BEFORE_MS = 60 * 1000;        // warn 1 minute before logout

  const handleLogout = async () => {
    await logout();
    navigate('/SIMS', { replace: true });
  };

  const resetIdleTimer = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    setShowIdleWarning(false);

    warningTimerRef.current = setTimeout(() => {
      setShowIdleWarning(true);
    }, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);

    idleTimerRef.current = setTimeout(() => {
      handleLogout();
    }, IDLE_TIMEOUT_MS);
  };

  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(e => window.addEventListener(e, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetIdleTimer));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    };
  }, []);

  // Presence heartbeat — sends current page every 60s so Jadell can see who is logged in
  // Uses raw fetch (not the shared axios instance) so interceptor noise is avoided on 404
  useEffect(() => {
    if (!user) return;
    const sendHeartbeat = async () => {
      const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) || '/api';
      const token = await freshAccessToken();
      const userStr = sessionStorage.getItem('user');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (userStr) {
        try {
          const u = JSON.parse(userStr);
          headers['x-dev-user-id'] = u.email;
          headers['x-dev-user-name'] = u.name || u.email;
        } catch { /* ignore */ }
      }
      fetch(`${base}/presence/heartbeat`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ page: location.pathname }),
      }).catch(() => {});
    };
    void sendHeartbeat();
    const interval = setInterval(() => { void sendHeartbeat(); }, 60_000);
    return () => clearInterval(interval);
  }, [location.pathname, user?.email]);

  if (!isAuthenticated()) {
    return <Navigate to="/SIMS" replace />;
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>
      {/* Idle warning overlay */}
      {showIdleWarning && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: 'var(--card-bg, #1e2a3a)',
            border: '1px solid var(--card-border, #2d3f55)',
            borderRadius: '12px',
            padding: '36px 40px',
            maxWidth: '420px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
          }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏱️</div>
            <h2 style={{ margin: '0 0 10px', color: 'var(--text-primary, #fff)', fontSize: '20px' }}>
              Session Expiring Soon
            </h2>
            <p style={{ margin: '0 0 24px', color: 'var(--text-secondary, #aaa)', fontSize: '14px', lineHeight: 1.6 }}>
              You've been inactive for 14 minutes. You'll be logged out in <strong>1 minute</strong> unless you continue.
            </p>
            <button
              onClick={resetIdleTimer}
              style={{
                padding: '11px 32px',
                background: 'var(--accent, #2196f3)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                marginRight: '12px'
              }}
            >
              Stay Logged In
            </button>
            <button
              onClick={handleLogout}
              style={{
                padding: '11px 24px',
                background: 'transparent',
                color: 'var(--text-secondary, #aaa)',
                border: '1px solid var(--card-border, #2d3f55)',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Logout Now
            </button>
          </div>
        </div>
      )}
      <header style={{
        background: 'linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-primary) 100%)',
        color: 'var(--text-primary)',
        padding: '20px 40px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '2px solid var(--card-border)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <div style={{
            width: '150px',
            height: '150px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <img 
              src={getLogoPath(currentTheme)} 
              alt="Laurus College" 
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: 'var(--accent)' }}>
              SIMS - Student Information Management System
            </h1>
            <p style={{ margin: '2px 0 0 0', fontSize: '13px', opacity: 0.85, color: 'var(--text-secondary)' }}>
              {user && `Welcome, ${user.name} (${((user as any).roles?.length > 1 ? (user as any).roles : [user.role]).join(', ')})`}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <ThemeSelector currentTheme={currentTheme} onThemeChange={handleThemeChange} />
          {/* Show "My Dashboards" hub button only for non-admin multi-role users */}
          {(() => {
            const allRoles: string[] = (user as any)?.roles?.length
              ? (user as any).roles
              : (user?.role ? [user.role] : []);
            const knownRoutes = ['Survey.Admin','Survey.Agent','Survey.Creator','Campaign.Manager','Drip.Manager','Survey.Viewer','Teachers','Faculty','Scheduler.Admin','Scheduler.Instructor','Graduate.Admin','Graduate.Viewer'];
            const adminRoles = ['Survey.Admin', 'Survey.Agent', 'Scheduler.Admin'];
            const isAdmin = allRoles.some(r => adminRoles.includes(r));
            return !isAdmin && allRoles.filter(r => knownRoutes.includes(r)).length > 1 ? (
              <button
                onClick={() => navigate('/Dashboard')}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--card-border)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.3s',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--card-bg)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                ⊞ My Dashboards
              </button>
            ) : null;
          })()}
          <button
            onClick={handleLogout}
            style={{
              padding: '10px 24px',
              background: 'var(--button-bg)',
              color: 'var(--button-text)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              transition: 'all 0.3s'
            }}
          >
            Logout
          </button>
        </div>
      </header>

      <main style={{ maxWidth: '1200px', margin: '2rem auto', padding: '0 1rem' }}>
        {children}
      </main>

      <footer style={{
        borderTop: '1px solid var(--card-border)',
        marginTop: '3rem',
        padding: '1.25rem 2rem',
        textAlign: 'center',
        background: 'var(--bg-secondary)',
      }}>
        <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '2px' }}>
          Laurus College — Student Information Management System
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          © {new Date().getFullYear()} Laurus College. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

function getMuiTheme(themeObj: any) {
  return createTheme({
    palette: {
          mode: themeObj.colors.textPrimary === '#ffffff' ? 'dark' : 'light',
      background: {
        default: themeObj.colors.bgPrimary,
        paper: themeObj.colors.cardBg,
      },
      primary: {
        main: themeObj.colors.accent,
      },
      secondary: {
        main: themeObj.colors.accentHover,
      },
      text: {
        primary: themeObj.colors.textPrimary,
        secondary: themeObj.colors.textSecondary,
      },
    },
    typography: {
      fontWeightBold: 700,
    },
  });
}

interface AppRoutesProps {
  currentTheme: string;
  pendingDashboardNav: boolean;
  setPendingDashboardNav: (val: boolean) => void;
  handleThemeChange: (themeName: string) => void;
}
function AppRoutes({ currentTheme, pendingDashboardNav, setPendingDashboardNav, handleThemeChange }: AppRoutesProps) {
  const navigate = useNavigate();
  // True while we're processing an OAuth redirect response — prevents flashing /SIMS before
  // the token is stored and navigation to /Dashboard happens.
  const [isProcessingAuth, setIsProcessingAuth] = useState(() => sessionStorage.getItem('msal_redirect_pending') === '1');

  // Apply theme immediately on mount and whenever it changes
  useEffect(() => {
    console.log('[App.tsx] Applying theme:', currentTheme);
    applyTheme(currentTheme);
  }, [currentTheme]);
  
  // Also ensure theme is applied on any route change
  useEffect(() => {
    console.log('[App.tsx] Route mounted, ensuring theme is applied');
    applyTheme(getSelectedTheme());
  }, []);

  useEffect(() => {
    // Handle MSAL redirect callback and navigate if successful
    const processRedirect = async () => {
      try {
        console.log('[App.tsx] processRedirect START - Current path:', window.location.pathname);

        // If already authenticated, just handle routing — don't re-process the redirect
        if (isAuthenticated()) {
          if (window.location.pathname === '/') {
            navigate('/Dashboard', { replace: true });
          }
          return;
        }

        // Always call handleRedirectCallback — MSAL caches the redirect response internally
        // so this is safe to call even when the URL no longer contains ?code=
        // (main.tsx processes the code before React mounts and clears the URL)
        const user = await handleRedirectCallback();
        if (user) {
          console.log('[App.tsx] ✅ Redirect login successful, user:', user);
          // If we stored a post-login path (e.g. from ModoLabs login_hint flow), use it
          const postLoginPath = sessionStorage.getItem('post_login_path') || '/Dashboard';
          sessionStorage.removeItem('post_login_path');
          navigate(postLoginPath, { replace: true });
        } else {
          // No redirect response — check for ?login_hint= (e.g. from ModoLabs deep link)
          // and attempt Azure AD silent SSO before falling back to the login page.
          const loginHint = new URLSearchParams(window.location.search).get('login_hint');
          if (loginHint) {
            console.log('[App.tsx] Found login_hint param, attempting silent auth for:', loginHint);
            const silentUser = await attemptSilentAuth(loginHint);
            if (silentUser) {
              // Strip login_hint from URL then navigate to the originally requested path
              const targetPath = window.location.pathname || '/Dashboard';
              navigate(targetPath, { replace: true });
              return;
            }
            // Silent auth failed (WebView blocked iframe) — auto-trigger loginRedirect
            // with the hint so the student's account is pre-selected on the Microsoft page.
            // Store the intended destination so we return there after login.
            console.log('[App.tsx] Silent auth failed — auto-redirecting with login_hint:', loginHint);
            sessionStorage.setItem('msal_redirect_pending', '1');
            sessionStorage.setItem('post_login_path', window.location.pathname);
            const { msalInstance: msal } = await import('./auth/LoginButton');
            const { loginRequest: lr } = await import('./auth/msalConfig');
            await msal.loginRedirect({ ...lr, loginHint });
            return; // page redirects away
          }
          console.log('[App.tsx] No redirect user — showing current route');
        }
      } catch (error) {
        console.error('[App.tsx] ❌ Error processing redirect:', error);
      } finally {
        sessionStorage.removeItem('msal_redirect_pending');
        setIsProcessingAuth(false);
      }
    };
    
    processRedirect();
  }, [navigate]);

  const handleLoginSuccess = (userData: any) => {
    console.log('User logged in:', userData);
  };
  const handleLoginError = (error: Error) => {
    console.error('Login error:', error);
    alert('Login failed. Please try again.');
  };

  const muiTheme = useMemo(() => getMuiTheme(themes[currentTheme] || themes.midnight), [currentTheme]);

  // While processing an OAuth redirect (between redirect-back and token storage),
  // render nothing to avoid flashing the /SIMS login page.
  if (isProcessingAuth) {
    return <div style={{ minHeight: '100vh', background: 'var(--bg-gradient-start)' }} />;
  }

  return (
    <React.Fragment>
      <Routes>
        <Route path="/SIMS" element={(
          <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg-gradient-start)', position: 'relative', overflow: 'hidden' }}>
            {/* Left side - Branding and features */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '60px', background: 'none', position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', width: '100%', maxWidth: '420px', height: '540px', marginTop: '-5px' }}>
              {/* Top group: logo + title + subtitle — aligned to top of right card */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ position: 'relative', width: '160px', height: '160px', marginBottom: '-34px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {/* Centered Glow Behind Logo */}
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: '180px',
                  height: '180px',
                  background: 'radial-gradient(circle, rgba(255,215,0,0.18) 0%, transparent 70%)',
                  borderRadius: '50%',
                  filter: 'blur(32px)',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 0,
                  pointerEvents: 'none',
                }} />
                <img src={getLogoPath(currentTheme)} alt="Laurus College" style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'relative', zIndex: 1 }} />
              </div>
              <h1 style={{ fontSize: '42px', marginBottom: '10px', fontWeight: '700', background: 'linear-gradient(135deg, var(--accent) 0%, var(--text-primary) 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', letterSpacing: '-0.5px', lineHeight: '1.2' }}>SIMS</h1>
              <h2 style={{ fontSize: '20px', fontWeight: '400', color: 'var(--text-secondary)', margin: '0 0 1px 0', opacity: 0.85, letterSpacing: '0.5px' }}>Student Information Management System</h2>
              </div>
              {/* Bottom group: feature grid — aligned to bottom of right card */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', width: '100%', marginTop: '24px' }}>
                {[{ icon: '📊', text: 'Survey Management', desc: 'Create & manage' }, { icon: '📈', text: 'Analytics', desc: 'Real-time insights' }, { icon: '🔒', text: 'Secure Access', desc: 'Azure AD protected' }, { icon: '⚡', text: 'Fast & Efficient', desc: 'Streamlined workflow' }].map((feature, idx) => (
                  <div key={idx} style={{ background: 'var(--card-bg)', borderRadius: '16px', padding: '20px', border: '1px solid var(--card-border)', transition: 'all 0.3s ease', cursor: 'default', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                    <div style={{ fontSize: '28px', marginBottom: '10px' }}>{feature.icon}</div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>{feature.text}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', opacity: 0.7 }}>{feature.desc}</div>
                  </div>
                ))}
              </div>
              </div>
            </div>
            {/* Right side - Login card */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px', background: 'linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%)', backdropFilter: 'blur(20px)', borderLeft: '1px solid var(--card-border)', position: 'relative', zIndex: 1 }}>
              <div style={{ background: 'var(--card-bg)', borderRadius: '24px', padding: '50px 45px', boxShadow: '0 30px 80px rgba(0,0,0,0.4), 0 0 1px rgba(255,215,0,0.1)', maxWidth: '420px', width: '100%', border: '1px solid var(--card-border)', position: 'relative', animation: 'slideInRight 0.6s ease-out', zIndex: 2, marginTop: '28px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ width: '120px', height: '120px', margin: '0 auto 30px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-secondary)', borderRadius: '24px', padding: '8px', border: '1px solid var(--card-border)' }}>
                    <img src={getLogoPath(currentTheme)} alt="Laurus College" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  </div>
                  <h2 style={{ fontSize: '32px', marginBottom: '8px', color: 'var(--accent)', fontWeight: '700', textShadow: '0 0 30px rgba(255,215,0,0.2)', letterSpacing: '-0.5px' }}>Welcome</h2>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '45px', lineHeight: '1.5' }}>Sign in with your Microsoft account<br/>to access the portal</p>
                  {/* Ensure LoginButton is visible and functional */}
                  <LoginButton onLoginSuccess={handleLoginSuccess} onLoginError={handleLoginError} />
                </div>
                <div style={{ marginTop: '40px', paddingTop: '25px', borderTop: '1px solid rgba(255,215,0,0.15)', textAlign: 'center' }}>
                  <span style={{ fontSize: '16px' }}>🔒 Secured by Microsoft Azure AD</span>
                  <p style={{ margin: 0, fontSize: '11px', color: '#999', opacity: 0.7 }}>© 2014 - 2026 Laurus College. All rights reserved</p>
                </div>
              </div>
            </div>
            {/* CSS Animations */}
            <style>{`
              @keyframes fadeIn {
                from { opacity: 0; transform: translateY(20px); }
                to { opacity: 1; transform: translateY(0); }
              }
              @keyframes slideInRight {
                from { opacity: 0; transform: translateX(30px); }
                to { opacity: 1; transform: translateX(0); }
              }
              @keyframes pulse {
                0%, 100% { opacity: 0.8; transform: scale(1); }
                50% { opacity: 1; transform: scale(1.05); }
              }
            `}</style>
          </div>
        )} />
        {/* /Dashboard is the multi-role hub — it computes each user's own landing route
            (RoleBasedDashboard) and never renders privileged content itself, so it needs
            only auth, not a role allow-list. Every other route below is sealed by RoleRoute,
            which re-checks roles on every render (back/forward/direct-URL/redirect proof). */}
        <Route path="/Dashboard" element={<ProtectedRoute currentTheme={currentTheme} handleThemeChange={handleThemeChange}><RoleBasedDashboard /></ProtectedRoute>} />
        <Route path="/Surveys/creator" element={<RoleRoute allow={['Survey.Creator','Survey.Admin']}><ProtectedRoute currentTheme={currentTheme} handleThemeChange={handleThemeChange}><CreatorDashboard /></ProtectedRoute></RoleRoute>} />
        <Route path="/Surveys/teacher" element={<RoleRoute allow={['Teachers','Survey.Admin']}><ProtectedRoute currentTheme={currentTheme} handleThemeChange={handleThemeChange}><TeacherDashboard /></ProtectedRoute></RoleRoute>} />
        <Route path="/Surveys/faculty" element={<RoleRoute allow={['Faculty','Survey.Admin']}><ProtectedRoute currentTheme={currentTheme} handleThemeChange={handleThemeChange}><FacultyDashboard /></ProtectedRoute></RoleRoute>} />
        <Route path="/Surveys/view" element={<RoleRoute allow={['Survey.Viewer','Survey.Admin','Survey.Agent']}><ProtectedRoute currentTheme={currentTheme} handleThemeChange={handleThemeChange}><ViewerDashboard /></ProtectedRoute></RoleRoute>} />
        <Route path="/Campaigns" element={<RoleRoute allow={['Campaign.Manager','Survey.Admin']}><ProtectedRoute currentTheme={currentTheme} handleThemeChange={handleThemeChange}><CampaignManagerDashboard /></ProtectedRoute></RoleRoute>} />
        {/* Teachers & Faculty author/manage their OWN surveys via the shared create/edit/responses
            screens (their dashboards link here) — they must be allowed, or they are locked out of
            their own surveys. The full /Surveys list stays Admin/Agent/Creator only. */}
        <Route path="/Surveys" element={<RoleRoute allow={['Survey.Admin','Survey.Agent','Survey.Creator']}><ProtectedRoute currentTheme={currentTheme} handleThemeChange={handleThemeChange}><SurveysPage /></ProtectedRoute></RoleRoute>} />
        <Route path="/Surveys/create" element={<RoleRoute allow={['Survey.Admin','Survey.Agent','Survey.Creator','Teachers','Faculty']}><ProtectedRoute currentTheme={currentTheme} handleThemeChange={handleThemeChange}><CreateSurveyPage /></ProtectedRoute></RoleRoute>} />
        <Route path="/Surveys/edit/:id" element={<RoleRoute allow={['Survey.Admin','Survey.Agent','Survey.Creator','Teachers','Faculty']}><ProtectedRoute currentTheme={currentTheme} handleThemeChange={handleThemeChange}><EditSurveyPage /></ProtectedRoute></RoleRoute>} />
        <Route path="/Surveys/responses/:id" element={<RoleRoute allow={['Survey.Admin','Survey.Agent','Survey.Creator','Survey.Viewer','Teachers','Faculty']}><ProtectedRoute currentTheme={currentTheme} handleThemeChange={handleThemeChange}><ResponsesPage /></ProtectedRoute></RoleRoute>} />
        {/* Campaign Dashboard (ECM): SIMS Admin + Campaign Manager ONLY. Survey.Agent and
            Survey.Creator are intentionally excluded. RoleRoute gates the whole Outlet subtree
            before ECMRoutesWrapper's layout/idle-timer mount. */}
        <Route path="/ECM" element={<RoleRoute allow={['Survey.Admin','Campaign.Manager','Drip.Manager','Graduate.Admin','Graduate.Viewer']}><ECMRoutesWrapper /></RoleRoute>}>
          <Route index element={<Navigate to="/ECM/Dashboard" replace />} />
          <Route path="Dashboard" element={<ECMDashboard />} />
          <Route path="Dashboard/graduates" element={<ECMDashboard />} />
          <Route path="Dashboard/templates" element={<ECMDashboard />} />
          <Route path="Dashboard/new-campaign" element={<ECMDashboard />} />
          {/* The retired Campaign Portal > SendGrid Templates sub-tab's address, kept for old links:
              ECMDashboard replaces it with Dashboard/new-campaign (New Campaign). Staff create
              campaign templates in SendGrid now. */}
          <Route path="Dashboard/new-campaign/sendgrid-templates" element={<ECMDashboard />} />
          <Route path="Dashboard/drip" element={<ECMDashboard />} />
          {/* Drip Campaign > Drip Layout Template (Survey.Admin, Drip.Manager). */}
          <Route path="Dashboard/drip/layout-template" element={<ECMDashboard />} />
          {/* Drip Campaign > Verity Leads and > Website Leads: not in Verity (Survey.Admin, Drip.Manager). */}
          <Route path="Dashboard/drip/leads" element={<ECMDashboard />} />
          <Route path="Dashboard/drip/website" element={<ECMDashboard />} />
          {/* Their retired top-level tab addresses, kept for old links and the docs: ECMDashboard (and, for
              a drip-only role, ECMRoutesWrapper) redirects each to its Drip Campaign sub-tab. */}
          <Route path="Dashboard/leads" element={<ECMDashboard />} />
          <Route path="Dashboard/website" element={<ECMDashboard />} />
          {/* The retired Templates tab's address, kept for old links: ECMDashboard redirects it to
              Campaign Portal (New Campaign), or for a drip-only role to the drip layout. Not the
              dead Dashboard/templates path above. */}
          <Route path="Dashboard/email-templates" element={<ECMDashboard />} />
          <Route path="Dashboard/scheduler" element={<ECMDashboard />} />
          <Route path="Dashboard/scheduler/data" element={<ECMDashboard />} />
          <Route path="Dashboard/scheduler/data/:subTab" element={<ECMDashboard />} />
          <Route path="Dashboard/scheduler/audit" element={<ECMDashboard />} />
          <Route path="Dashboard/scheduler/audit/:auditSubTab" element={<ECMDashboard />} />
          <Route path="Dashboard/scheduler/activity" element={<ECMDashboard />} />
          <Route path="new-campaign" element={<NewCampaign />} />
          <Route path="job/:id" element={<JobDetails />} />
        </Route>
        <Route path="/Scheduler" element={<RoleRoute allow={['Scheduler.Admin','Survey.Admin']}><ProtectedRoute currentTheme={currentTheme} handleThemeChange={handleThemeChange}><SchedulerAdminDashboard /></ProtectedRoute></RoleRoute>} />
        <Route path="/Scheduler/data" element={<RoleRoute allow={['Scheduler.Admin','Survey.Admin']}><ProtectedRoute currentTheme={currentTheme} handleThemeChange={handleThemeChange}><SchedulerAdminDashboard /></ProtectedRoute></RoleRoute>} />
        <Route path="/Scheduler/data/:subTab" element={<RoleRoute allow={['Scheduler.Admin','Survey.Admin']}><ProtectedRoute currentTheme={currentTheme} handleThemeChange={handleThemeChange}><SchedulerAdminDashboard /></ProtectedRoute></RoleRoute>} />
        <Route path="/Scheduler/audit" element={<RoleRoute allow={['Scheduler.Admin','Survey.Admin']}><ProtectedRoute currentTheme={currentTheme} handleThemeChange={handleThemeChange}><SchedulerAdminDashboard /></ProtectedRoute></RoleRoute>} />
        <Route path="/Scheduler/audit/:auditSubTab" element={<RoleRoute allow={['Scheduler.Admin','Survey.Admin']}><ProtectedRoute currentTheme={currentTheme} handleThemeChange={handleThemeChange}><SchedulerAdminDashboard /></ProtectedRoute></RoleRoute>} />
        <Route path="/Scheduler/activity" element={<RoleRoute allow={['Scheduler.Admin','Survey.Admin']}><ProtectedRoute currentTheme={currentTheme} handleThemeChange={handleThemeChange}><SchedulerAdminDashboard /></ProtectedRoute></RoleRoute>} />
        <Route path="/Scheduler/instructor" element={<RoleRoute allow={['Scheduler.Instructor','Scheduler.Admin','Survey.Admin']}><ProtectedRoute currentTheme={currentTheme} handleThemeChange={handleThemeChange}><InstructorScheduleView /></ProtectedRoute></RoleRoute>} />
        <Route path="/Scheduler/constraints" element={<RoleRoute allow={['Scheduler.Admin','Survey.Admin']}><ProtectedRoute currentTheme={currentTheme} handleThemeChange={handleThemeChange}><ConstraintsEditor /></ProtectedRoute></RoleRoute>} />
        <Route path="/EnrollmentVerification" element={<RoleRoute allow={['Student','Survey.Admin','Scheduler.Admin']}><ProtectedRoute currentTheme={currentTheme} handleThemeChange={handleThemeChange}><EnrollmentVerification /></ProtectedRoute></RoleRoute>} />
        {/* /ev is the intentional public (unauthenticated) enrollment-verification bypass — no guard. */}
        <Route path="/ev" element={<EnrollmentVerificationBypass />} />
        <Route path="/Student" element={<RoleRoute allow={['Student','Survey.Admin']}><ProtectedRoute currentTheme={currentTheme} handleThemeChange={handleThemeChange}><StudentDashboard /></ProtectedRoute></RoleRoute>} />
        <Route path="/" element={<Navigate to={isAuthenticated() ? "/Dashboard" : "/SIMS"} replace />} />
        <Route path="*" element={<Navigate to={isAuthenticated() ? "/Dashboard" : "/SIMS"} replace />} />
      </Routes>
    </React.Fragment>
  );
}

function SurveysPage() {
  const navigate = useNavigate();
  return (
    <SurveyList
      onCreateNew={() => navigate('/Surveys/create')}
      onEditSurvey={id => navigate(`/Surveys/edit/${id}`)}
      onViewResponses={id => navigate(`/Surveys/responses/${id}`)}
    />
  );
}

function CreateSurveyPage() {
  const navigate = useNavigate();
  // Return the user to their own home (Admin/Agent → /Surveys, Creator → /Surveys/creator).
  const home = getHomeRoute(getCurrentUser());
  return (
    <CreateSurvey
      onCancel={() => navigate(home, { replace: true })}
      onSurveyCreated={() => navigate(home, { replace: true })}
    />
  );
}

function EditSurveyPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const home = getHomeRoute(getCurrentUser());
  return (
    <EditSurvey
      surveyId={id || ''}
      onBack={() => navigate(home, { replace: true })}
    />
  );
}

function ResponsesPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  // Role-aware exit: a Survey.Viewer returns to /Surveys/view (read-only), NOT the
  // full read/write /Surveys list — fixes the leak where exiting responses dropped a
  // Viewer onto the agent/admin survey view. `replace` also collapses the
  // view → responses → home history so back/forward can't re-walk into it.
  const home = getHomeRoute(getCurrentUser());
  return (
    <ViewResponses
      surveyId={id || ''}
      onClose={() => navigate(home, { replace: true })}
    />
  );
}

function App() {
  const [pendingDashboardNav, setPendingDashboardNav] = useState<boolean>(false);
  const [currentTheme, setCurrentTheme] = useState<string>(getSelectedTheme());

  // Theme change handler
  const handleThemeChange = (themeName: string) => setCurrentTheme(themeName);

  return (
    <PendingDashboardNavContext.Provider value={{ pendingDashboardNav, setPendingDashboardNav }}>
      <ThemeProvider theme={getMuiTheme(themes[currentTheme] || themes.midnight)}>
        <CssBaseline />
        <BrowserRouter>
          <AppRoutes currentTheme={currentTheme} pendingDashboardNav={pendingDashboardNav} setPendingDashboardNav={setPendingDashboardNav} handleThemeChange={handleThemeChange} />
        </BrowserRouter>
      </ThemeProvider>
    </PendingDashboardNavContext.Provider>
  );
}

export default App;

